import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Form, 
  Button, 
  Pagination, 
  Spinner, 
  Alert,
  Badge,
  Modal,
  Toast,
  ToastContainer
} from 'react-bootstrap';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { dashboardService, User } from '../../services/dashboardService';
import { appNotificationService } from '../../services/appNotificationService';
import { walletService, WalletBalance } from '../../services/walletService';
import { getRoleDisplayLabel, MERCHANT_LABEL, PROCESSOR_LABEL, SUB_MERCHANT_LABEL } from '../../utils/displayLabels';
import InternalPageHeader from '../Layout/InternalPageHeader';
import { resetScrollPosition } from '../Layout/RouteScrollReset';
import { resolveSafeOperatorRoute } from '../../utils/internalRouteGuard';
import { sortByNewest } from '../../utils/sortByNewest';
import {
  browserAlertsSupported,
  getBrowserAlertPermission,
  getPaymentToneEnabled,
  playPaymentTone,
  requestBrowserAlertPermission,
  sendBrowserAlert,
  setPaymentToneEnabled,
} from '../../utils/browserAlerts';

interface TransactionFees {
  superAgentPercent: number;
  agentPercent: number;
  superAgent: number;
  agent: number;
  netPayout: number;
}

interface Payout {
  id: number;
  transactionId: number;
  type: 'Company' | 'Admin' | 'Agent' | 'SuperAgent' | 'Unknown';
  toWallet: string;
  amount: string;
  createtAt: string;
  createtBy: string;
  txHash: string;
  status: string;
}

interface DisplayPayout extends Payout {
  displayType: string;
}

interface CheckoutSession {
  id: string;
  transactionId: number;
  ramp?: string;
  isEnabled?: boolean;
  paymentMethod: string;
  url: string;
  status: string;
  createdAt: string;
  expireAt: string;
}

interface TransactionEvent {
  id: number;
  type: string;
  source: string;
  checkoutSessionId?: string;
  payload?: Record<string, unknown> | null;
  createdAt: string;
}

interface WebhookDelivery {
  id: number;
  action: string;
  source: string;
  eventId?: number | null;
  checkoutSessionId?: string | null;
  status: string;
  attemptCount: number;
  responseStatusCode?: number | null;
  responseBody?: string | null;
  lastErrorCode?: string | null;
  lastErrorMessage?: string | null;
  lastAttemptAt?: string | null;
  nextAttemptAt?: string | null;
  deliveredAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TransactionCaseNote {
  id: number;
  transactionId: number;
  authorUserId: number;
  authorName: string;
  type: string;
  caseStatus?: string | null;
  note: string;
  createdAt: string;
}

interface TransactionCaseDetails {
  transactionId: number;
  caseStatus: string;
  caseOwnerUserId?: number | null;
  caseOwnerName?: string | null;
  caseDueAt?: string | null;
  reminderEnabled: boolean;
  escalationEnabled: boolean;
  reminderSentAt?: string | null;
  escalatedAt?: string | null;
  caseNotes: TransactionCaseNote[];
}

interface CaseWorkflowDraft {
  transactionId: number | null;
  caseOwnerUserId: string;
  caseDueAt: string;
  reminderEnabled: boolean;
  escalationEnabled: boolean;
  note: string;
}

interface MerchantReferences {
  merchantTransactionId?: string | null;
  merchantUserId?: string | null;
  merchantSessionId?: string | null;
  idempotencyKey?: string | null;
  metadata?: Record<string, string | null>;
}

interface Transaction {
  id: number;
  userId: number;
  provider: string;
  fiatCurrency: string;
  fiatAmount: number;
  cryptoCurrency: string;
  cryptoAmount: number;
  receivedCryptoAmount?: number | null;
  distributedCryptoAmount?: number | null;
  userWallet: string;
  uniqueWalletAddress: string;
  publicPaymentId?: string;
  merchantReferences?: MerchantReferences | null;
  paymentSource?: string;
  displayStatus?: string;
  caseStatus?: string;
  caseOwnerUserId?: number | null;
  caseOwnerName?: string | null;
  caseDueAt?: string | null;
  caseReminderEnabled?: boolean;
  caseEscalationEnabled?: boolean;
  caseReminderSentAt?: string | null;
  caseEscalatedAt?: string | null;
  status: 'Issued' | 'Completed' | 'Pending' | 'Failed' | 'PayoutCompleted' | 'Expired';
  timestamp: string;
  fees: TransactionFees;
  payouts?: Payout[];
  checkoutSessions?: CheckoutSession[];
  events?: TransactionEvent[];
  webhookDeliveries?: WebhookDelivery[];
}

interface TransactionFilters {
  status: string;
  exceptionType: string;
  caseStatus: string;
  workflowState: string;
  caseOwnerUserId: string;
  dateFrom: string;
  dateTo: string;
  userId: string;
  search: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const buildTransactionListSignature = (transactions: Transaction[]) => JSON.stringify(
  transactions.map((transaction) => ({
    id: transaction.id,
    status: transaction.status,
    displayStatus: transaction.displayStatus,
    paymentSource: transaction.paymentSource,
    provider: transaction.provider,
    timestamp: transaction.timestamp,
    publicPaymentId: transaction.publicPaymentId,
    fiatAmount: transaction.fiatAmount,
    fiatCurrency: transaction.fiatCurrency,
    cryptoAmount: transaction.cryptoAmount,
    receivedCryptoAmount: transaction.receivedCryptoAmount,
    distributedCryptoAmount: transaction.distributedCryptoAmount,
  })),
);

const buildPaginationSignature = (pagination: PaginationInfo) => JSON.stringify(pagination);

const getTransactionFiltersFromSearch = (search: string): TransactionFilters => {
  const params = new URLSearchParams(search);

  return {
    status: params.get('status') || '',
    exceptionType: params.get('exceptionType') || '',
    caseStatus: params.get('caseStatus') || '',
    workflowState: params.get('workflowState') || '',
    caseOwnerUserId: params.get('caseOwnerUserId') || '',
    dateFrom: params.get('dateFrom') || '',
    dateTo: params.get('dateTo') || '',
    userId: params.get('userId') || '',
    search: params.get('search') || '',
  };
};

const getOpenTransactionIdFromSearch = (search: string): number | null => {
  const raw = new URLSearchParams(search).get('openTransactionId');
  if (!raw) {
    return null;
  }

  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
};

const decodeReturnPath = (rawValue: string | null): string | null => {
  if (!rawValue) {
    return null;
  }

  try {
    return decodeURIComponent(rawValue);
  } catch {
    return rawValue;
  }
};

const getSafeReturnToFromSearch = (search: string): string | null => {
  const decoded = decodeReturnPath(new URLSearchParams(search).get('returnTo'));
  if (!decoded) {
    return null;
  }

  return resolveSafeOperatorRoute(decoded);
};

const cleanupModalArtifacts = () => {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.querySelector('.modal.show')) {
    return;
  }

  document.body.classList.remove('modal-open');
  document.body.style.removeProperty('overflow');
  document.body.style.removeProperty('padding-right');
};

const areTransactionFiltersEqual = (left: TransactionFilters, right: TransactionFilters): boolean => {
  return left.status === right.status &&
    left.exceptionType === right.exceptionType &&
    left.caseStatus === right.caseStatus &&
    left.workflowState === right.workflowState &&
    left.caseOwnerUserId === right.caseOwnerUserId &&
    left.dateFrom === right.dateFrom &&
    left.dateTo === right.dateTo &&
    left.userId === right.userId &&
    left.search === right.search;
};

const getPayoutSortOrder = (type: Payout['type']) => {
  switch (type) {
    case 'Company':
      return 0;
    case 'Admin':
      return 1;
    case 'SuperAgent':
      return 2;
    case 'Agent':
      return 3;
    default:
      return 4;
  }
};

const buildDisplayPayouts = (payouts: Payout[]): DisplayPayout[] => {
  const sorted = [...payouts].sort((left, right) => {
    const orderDelta = getPayoutSortOrder(left.type) - getPayoutSortOrder(right.type);
    if (orderDelta !== 0) {
      return orderDelta;
    }

    return left.id - right.id;
  });

    const visible = sorted.filter((payout) => payout.type !== 'Company');

  return visible.map((payout, index) => {
    let displayType: string = payout.type;

    if (visible.length === 1) {
      displayType =
        payout.type === 'Admin' ? PROCESSOR_LABEL :
        payout.type === 'SuperAgent' ? MERCHANT_LABEL :
        payout.type === 'Agent' ? SUB_MERCHANT_LABEL :
        payout.type;
    } else if (visible.length === 2) {
      displayType = index === 0 ? PROCESSOR_LABEL : MERCHANT_LABEL;
    } else {
      displayType =
        index === 0 ? PROCESSOR_LABEL :
        index === visible.length - 1 ? SUB_MERCHANT_LABEL :
        MERCHANT_LABEL;
    }

    return {
      ...payout,
      displayType,
    };
  });
};

const TransactionList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TransactionFilters>(() => getTransactionFiltersFromSearch(window.location.search));
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const pageSize = 10; // Server-side page size
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showUserDropdown, setShowUserDropdown] = useState<boolean>(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showTransactionModal, setShowTransactionModal] = useState<boolean>(false);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<number[]>([]);
  const [selectedCaseDetails, setSelectedCaseDetails] = useState<TransactionCaseDetails | null>(null);
  const [caseNoteDraft, setCaseNoteDraft] = useState<string>('');
  const [caseWorkflowDraft, setCaseWorkflowDraft] = useState<CaseWorkflowDraft>({
    transactionId: null,
    caseOwnerUserId: '',
    caseDueAt: '',
    reminderEnabled: false,
    escalationEnabled: false,
    note: '',
  });
  const [caseWorkflowDirty, setCaseWorkflowDirty] = useState<boolean>(false);
  const [caseActionLoading, setCaseActionLoading] = useState<boolean>(false);
  const [bulkCaseStatus, setBulkCaseStatus] = useState<string>('InReview');
  const [bulkCaseNote, setBulkCaseNote] = useState<string>('');
  const [bulkActionLoading, setBulkActionLoading] = useState<boolean>(false);
  const [walletBalances, setWalletBalances] = useState<{ [address: string]: WalletBalance }>({});
  const [loadingBalances, setLoadingBalances] = useState<boolean>(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const [liveToast, setLiveToast] = useState<{ title: string; message: string } | null>(null);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>(() => getBrowserAlertPermission());
  const [paymentToneEnabled, setPaymentToneState] = useState<boolean>(() => getPaymentToneEnabled());
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(() => {
    const initialFilters = getTransactionFiltersFromSearch(window.location.search);
    return Boolean(
      initialFilters.dateFrom ||
      initialFilters.dateTo ||
      initialFilters.caseStatus ||
      initialFilters.workflowState ||
      initialFilters.caseOwnerUserId
    );
  });
  const [expandedSections, setExpandedSections] = useState<{
    generatedLinks: boolean;
    wallets: boolean;
    merchantReferences: boolean;
    webhookDeliveries: boolean;
    settlements: boolean;
  }>({
    generatedLinks: false,
    wallets: false,
    merchantReferences: false,
    webhookDeliveries: false,
    settlements: false,
  });
  const previousStatusesRef = useRef<Map<number, string>>(new Map());
  const transactionsSignatureRef = useRef<string>('');
  const paginationSignatureRef = useRef<string>('');
  const lastOpenedFromQueryRef = useRef<number | null>(null);
  const filtersRef = useRef<TransactionFilters>(filters);
  const initialScrollResetPendingRef = useRef(true);

  const userRole = getCurrentUserRole() as 'Admin' | 'SuperAgent' | 'Agent' | null;
  const userId = getCurrentUserId();

  const fetchUsers = useCallback(async () => {
    try {
      const usersData = await dashboardService.getUsers();
      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    
    // Convert local datetime to UTC ISO string
    const date = new Date(dateString);
    return date.toISOString();
  };

  const formatDateForInput = (dateString?: string | null): string => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const buildTransactionQueryParams = useCallback((page?: number, includePagination: boolean = true) => {
    const params = new URLSearchParams();

    if (includePagination) {
      params.append('page', (page ?? 1).toString());
      params.append('pageSize', pageSize.toString());
    }

    if (filters.status) params.append('status', filters.status);
    if (filters.exceptionType) params.append('exceptionType', filters.exceptionType);
    if (filters.caseStatus) params.append('caseStatus', filters.caseStatus);
    if (filters.workflowState) params.append('workflowState', filters.workflowState);
    if (filters.caseOwnerUserId) params.append('caseOwnerUserId', filters.caseOwnerUserId);
    if (filters.dateFrom) params.append('dateFrom', formatDateForAPI(filters.dateFrom));
    if (filters.dateTo) params.append('dateTo', formatDateForAPI(filters.dateTo));
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.search.trim()) params.append('search', filters.search.trim());

    return params;
  }, [filters, pageSize]);

  const downloadCsv = useCallback(async (path: string, defaultFileName: string) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(path, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.expandedError?.message || errorData?.message || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const fileNameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
    const fileName = fileNameMatch?.[1] || defaultFileName;

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  const fetchTransactions = useCallback(async (page: number = 1, silent: boolean = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const params = buildTransactionQueryParams(page);


      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Error response:', errorData);
        
        // Extract meaningful error message
        let errorMessage = response.status === 429
          ? 'Live refresh is temporarily rate-limited. The list will retry automatically.'
          : `HTTP error! status: ${response.status}`;
        if (errorData?.expandedError?.message) {
          errorMessage = errorData.expandedError.message;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Handle different response formats
      // Check if response has pagination metadata
      let transactionArray: Transaction[] = [];
      let totalItems = 0;
      let totalPages = 1;
      
      if (Array.isArray(data)) {
        // Simple array response
        transactionArray = data;
        // If we got a full page, assume there might be more pages
        const hasMorePages = transactionArray.length === pageSize;
        totalItems = hasMorePages 
          ? (page * pageSize) + 1 // Estimate: at least one more item exists
          : ((page - 1) * pageSize) + transactionArray.length;
        totalPages = hasMorePages ? page + 1 : page;
      } else if (data && typeof data === 'object') {
        // Check if response has data and pagination properties
        if (Array.isArray(data.data)) {
          transactionArray = data.data;
        } else if (Array.isArray(data.transactions)) {
          transactionArray = data.transactions;
        } else if (Array.isArray(data.items)) {
          transactionArray = data.items;
        }
        
        // Check for pagination metadata
        if (data.pagination) {
          totalItems = data.pagination.totalItems || data.pagination.total || 0;
          totalPages = data.pagination.totalPages || Math.ceil(totalItems / pageSize) || 1;
        } else if (typeof data.count === 'number') {
          totalItems = data.count;
          const effectivePageSize = typeof data.pageSize === 'number' && data.pageSize > 0 ? data.pageSize : pageSize;
          totalPages = Math.max(1, Math.ceil(totalItems / effectivePageSize));
        } else if (data.total !== undefined) {
          totalItems = data.total;
          totalPages = Math.ceil(totalItems / pageSize);
        } else {
          // Fallback: use heuristic
          const hasMorePages = transactionArray.length === pageSize;
          totalItems = hasMorePages 
            ? (page * pageSize) + 1
            : ((page - 1) * pageSize) + transactionArray.length;
          totalPages = hasMorePages ? page + 1 : page;
        }
      }
      
      const sortedTransactions = sortByNewest(transactionArray, {
        dateKeys: ['timestamp'],
        numericFallbackKeys: ['id'],
      });

      const previousStatuses = previousStatusesRef.current;
      const nextStatuses = new Map<number, string>();
      let changedTransaction: Transaction | undefined;

      sortedTransactions.forEach((transaction) => {
        const signature = `${transaction.status}|${transaction.displayStatus || transaction.status}`;
        nextStatuses.set(transaction.id, signature);

        const previousSignature = previousStatuses.get(transaction.id);
        if (!changedTransaction && previousSignature && previousSignature !== signature) {
          changedTransaction = transaction;
        }
      });

      previousStatusesRef.current = nextStatuses;

      const nextTransactionSignature = buildTransactionListSignature(sortedTransactions);
      const transactionsChanged = nextTransactionSignature !== transactionsSignatureRef.current;
      transactionsSignatureRef.current = nextTransactionSignature;

      if (!silent || transactionsChanged) {
        setTransactions(sortedTransactions);
        setLastRefreshedAt(new Date().toISOString());
      }

      if (changedTransaction !== undefined) {
        const nextLabel = changedTransaction.displayStatus || changedTransaction.status;
        const isProcessed = isSettledStatus(changedTransaction.status);
        setLiveToast({
          title: isProcessed ? 'Payment received' : 'Transaction updated',
          message: `Transaction #${changedTransaction.id} is now ${nextLabel}.`,
        });

        if (isProcessed) {
          sendBrowserAlert('Payment received', `Transaction #${changedTransaction.id} is now ${nextLabel}.`, `transactions-${changedTransaction.id}-${changedTransaction.status}`);
          void playPaymentTone();
        }
      }
      
      // Update pagination state
      const nextPagination: PaginationInfo = {
        currentPage: page,
        totalPages: totalPages,
        totalItems: totalItems,
        itemsPerPage: pageSize,
      };
      const nextPaginationSignature = buildPaginationSignature(nextPagination);

      if (!silent || nextPaginationSignature !== paginationSignatureRef.current) {
        paginationSignatureRef.current = nextPaginationSignature;
        setPagination(prev => ({
          ...prev,
          ...nextPagination,
        }));
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [buildTransactionQueryParams]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useLayoutEffect(() => {
    initialScrollResetPendingRef.current = true;
    resetScrollPosition();

    const frameId = window.requestAnimationFrame(() => {
      resetScrollPosition();
    });

    const timeoutId = window.setTimeout(() => {
      resetScrollPosition();
    }, 120);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!initialScrollResetPendingRef.current) {
      return undefined;
    }

    if (loading && transactions.length === 0) {
      return undefined;
    }

    resetScrollPosition();

    const frameId = window.requestAnimationFrame(() => {
      resetScrollPosition();
    });

    const timeoutId = window.setTimeout(() => {
      resetScrollPosition();
      initialScrollResetPendingRef.current = false;
    }, 180);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [loading, transactions.length]);

  useEffect(() => {
    const nextFilters = getTransactionFiltersFromSearch(location.search);
    if (areTransactionFiltersEqual(filtersRef.current, nextFilters)) {
      return;
    }

    setFilters(nextFilters);
    setPagination((previous) => ({
      ...previous,
      currentPage: 1,
    }));
  }, [location.search]);

  useEffect(() => {
    // Only fetch users for Admin and SuperAgent roles
    if (userRole === 'Admin' || userRole === 'SuperAgent') {
      void fetchUsers();
    }
  }, [fetchUsers, userRole]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    const handle = window.setInterval(() => {
      void fetchTransactions(pagination.currentPage, true);
    }, 15000);

    return () => window.clearInterval(handle);
  }, [fetchTransactions, pagination.currentPage]);

  useEffect(() => {
    if (!selectedTransaction) {
      return;
    }

    const refreshedSelection = transactions.find((transaction) => transaction.id === selectedTransaction.id);
    if (refreshedSelection) {
      setSelectedTransaction((previous) => ({
        ...refreshedSelection,
        caseStatus: previous?.caseStatus || refreshedSelection.caseStatus,
      }));
    }
  }, [selectedTransaction, transactions]);

  useEffect(() => {
    setSelectedTransactionIds((previous) =>
      previous.filter((transactionId) => transactions.some((transaction) => transaction.id === transactionId)));
  }, [transactions]);

  useEffect(() => {
    if (!selectedCaseDetails) {
      return;
    }

    if (!caseWorkflowDirty || caseWorkflowDraft.transactionId !== selectedCaseDetails.transactionId) {
      setCaseWorkflowDraft({
        transactionId: selectedCaseDetails.transactionId,
        caseOwnerUserId: selectedCaseDetails.caseOwnerUserId ? selectedCaseDetails.caseOwnerUserId.toString() : '',
        caseDueAt: formatDateForInput(selectedCaseDetails.caseDueAt),
        reminderEnabled: selectedCaseDetails.reminderEnabled,
        escalationEnabled: selectedCaseDetails.escalationEnabled,
        note: '',
      });
      setCaseWorkflowDirty(false);
    }
  }, [caseWorkflowDirty, caseWorkflowDraft.transactionId, selectedCaseDetails]);

  // Handle click outside to close user dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (filters.dateFrom || filters.dateTo || filters.caseStatus || filters.workflowState || filters.caseOwnerUserId) {
      setShowAdvancedFilters(true);
    }
  }, [filters.caseOwnerUserId, filters.caseStatus, filters.dateFrom, filters.dateTo, filters.workflowState]);


  const getFilteredUsers = (): User[] => {
    let filteredUsers: User[] = [];
    
    if (userRole === 'Admin') {
      filteredUsers = users; // Admin sees all users
    } else if (userRole === 'SuperAgent') {
      // SuperAgent sees self and underlying users (Agents)
      filteredUsers = users.filter(user => 
        user.id === parseInt(userId || '0') || 
        (user.role === 'Agent' && user.id !== parseInt(userId || '0'))
      );
    } else {
      // Agent sees only self
      filteredUsers = users.filter(user => user.id === parseInt(userId || '0'));
    }

    // Apply search filter
    if (searchTerm.trim()) {
      filteredUsers = filteredUsers.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filteredUsers;
  };

  const getSelectedUserDisplay = (): string => {
    const user = users.find(u => u.id === parseInt(filters.userId));
    if (user) {
      return `👤 ${user.name} (${user.role})`;
    }
    return 'All Users';
  };

  const getSelectedUserDisplayLabel = (): string => {
    const user = users.find(u => u.id === parseInt(filters.userId, 10));
    if (user) {
      return `${user.name} (${getRoleDisplayLabel(user.role)})`;
    }
    return 'All accounts';
  };

  void getSelectedUserDisplay;

  const handleUserSelect = (userId: string) => {
    setFilters(prev => ({ ...prev, userId }));
    setSearchTerm('');
    setShowUserDropdown(false);
  };

  const handleEnableBrowserAlerts = async () => {
    const permission = await requestBrowserAlertPermission();
    setNotificationPermission(permission);
  };

  const handleTogglePaymentTone = () => {
    const nextValue = !paymentToneEnabled;
    setPaymentToneState(nextValue);
    setPaymentToneEnabled(nextValue);
  };

  const handleFilterChange = (field: keyof TransactionFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const syncFiltersToUrl = useCallback((
    nextFilters: TransactionFilters,
    openTransactionId?: number | null,
    keepReturnTo: boolean = true
  ) => {
    const params = new URLSearchParams();
    const returnTo = keepReturnTo ? getSafeReturnToFromSearch(location.search) : null;

    if (nextFilters.status) params.set('status', nextFilters.status);
    if (nextFilters.exceptionType) params.set('exceptionType', nextFilters.exceptionType);
    if (nextFilters.caseStatus) params.set('caseStatus', nextFilters.caseStatus);
    if (nextFilters.workflowState) params.set('workflowState', nextFilters.workflowState);
    if (nextFilters.caseOwnerUserId) params.set('caseOwnerUserId', nextFilters.caseOwnerUserId);
    if (nextFilters.dateFrom) params.set('dateFrom', nextFilters.dateFrom);
    if (nextFilters.dateTo) params.set('dateTo', nextFilters.dateTo);
    if (nextFilters.userId) params.set('userId', nextFilters.userId);
    if (nextFilters.search.trim()) params.set('search', nextFilters.search.trim());
    if (openTransactionId && openTransactionId > 0) params.set('openTransactionId', openTransactionId.toString());
    if (returnTo) params.set('returnTo', returnTo);

    const query = params.toString();
    navigate({
      pathname: location.pathname,
      search: query ? `?${query}` : '',
    }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    syncFiltersToUrl(filters);
    fetchTransactions(1);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: '',
      exceptionType: '',
      caseStatus: '',
      workflowState: '',
      caseOwnerUserId: '',
      dateFrom: '',
      dateTo: '',
      userId: '',
      search: '',
    };
    setFilters(clearedFilters);
    setSearchTerm('');
    setShowAdvancedFilters(false);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    syncFiltersToUrl(clearedFilters);
    fetchTransactions(1);
  };

  const handlePageChange = (page: number) => {
    fetchTransactions(page);
  };

  const handleExportCsv = async () => {
    try {
      setError(null);
      const params = buildTransactionQueryParams(undefined, false);
      const queryString = params.toString();
      const endpoint = `${API_ENDPOINTS.TRANSACTIONS}/export/csv${queryString ? `?${queryString}` : ''}`;
      await downloadCsv(endpoint, 'transactions-export.csv');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export transactions');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const isSettledStatus = (status: string) => status === 'Completed' || status === 'PayoutCompleted';

  const getStatusBadge = (status: string, displayStatus?: string) => {
    const tone = isSettledStatus(status)
      ? 'success'
      : status === 'Pending'
        ? 'warning'
        : status === 'Issued'
          ? 'mixed'
          : status === 'Expired'
            ? 'neutral'
            : 'danger';

    return <span className={`app-status-pill app-status-pill--${tone}`}>{displayStatus || status}</span>;
  };

  const getCaseStatusLabel = (caseStatus?: string) => {
    switch ((caseStatus || '').toLowerCase()) {
      case 'inreview':
        return 'In Review';
      case 'resolved':
        return 'Resolved';
      default:
        return 'Open';
    }
  };

  const getCaseStatusBadge = (caseStatus?: string) => {
    const normalized = (caseStatus || 'Open').toLowerCase();
    const tone = normalized === 'resolved'
      ? 'success'
      : normalized === 'inreview'
        ? 'warning'
        : 'neutral';

    return <span className={`app-status-pill app-status-pill--${tone}`}>{getCaseStatusLabel(caseStatus)}</span>;
  };

  const getCaseNoteTypeLabel = (type: string) => {
    if (type === 'StatusChanged') {
      return 'Status change';
    }

    if (type === 'WorkflowUpdated') {
      return 'Workflow update';
    }

    return 'Case note';
  };

  const getCaseOwnerOptions = () => {
    if (userRole === 'Agent') {
      const currentId = parseInt(userId || '0');
      return currentId > 0
        ? [{
            id: currentId,
            name: getUsernameById(currentId),
          }]
        : [];
    }

    return users.map((user) => ({
      id: user.id,
      name: `${user.name} (${user.role})`,
    }));
  };

  const getCaseDueState = (caseDueAt?: string | null) => {
    if (!caseDueAt) {
      return null;
    }

    const dueDate = new Date(caseDueAt);
    if (Number.isNaN(dueDate.getTime())) {
      return null;
    }

    const deltaMs = dueDate.getTime() - Date.now();
    return {
      overdue: deltaMs < 0,
      label: deltaMs < 0
        ? `Overdue since ${dueDate.toLocaleString()}`
        : `Due ${dueDate.toLocaleString()}`,
    };
  };

  const isCaseResolved = (caseStatus?: string | null) => (caseStatus || '').toLowerCase() === 'resolved';

  const getTransactionRowColors = (transaction: Transaction) => {
    if (!isCaseResolved(transaction.caseStatus) && transaction.caseEscalatedAt) {
      return {
        base: 'rgba(127, 29, 29, 0.32)',
        hover: 'rgba(153, 27, 27, 0.42)',
      };
    }

    if (!isCaseResolved(transaction.caseStatus) && getCaseDueState(transaction.caseDueAt)?.overdue) {
      return {
        base: 'rgba(120, 53, 15, 0.28)',
        hover: 'rgba(154, 52, 18, 0.38)',
      };
    }

    if (!isCaseResolved(transaction.caseStatus) && transaction.caseReminderSentAt) {
      return {
        base: 'rgba(30, 41, 59, 0.75)',
        hover: 'rgba(51, 65, 85, 0.9)',
      };
    }

    return {
      base: '#2d2d2d',
      hover: '#444',
    };
  };

  const formatTokenAmount = (value: unknown, currency: string = 'USDC') => {
    if (typeof value === 'number') {
      const usesSixDecimals = ['USDT', 'USDC'].includes(currency.toUpperCase());
      return `${value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: usesSixDecimals ? 6 : 2,
      })} ${currency}`;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      return `${value} ${currency}`;
    }

    return null;
  };

  const getReceivedCryptoAmount = (transaction: Transaction) => {
    return transaction.receivedCryptoAmount ?? transaction.cryptoAmount ?? null;
  };

  const getDistributedCryptoAmount = (transaction: Transaction) => {
    return transaction.distributedCryptoAmount ?? null;
  };

  const hasSettlementAmountMismatch = (transaction: Transaction) => {
    const receivedAmount = getReceivedCryptoAmount(transaction);
    const distributedAmount = getDistributedCryptoAmount(transaction);

    return receivedAmount !== null &&
      distributedAmount !== null &&
      Math.abs(receivedAmount - distributedAmount) >= 0.000001;
  };

  const getPayloadValue = (payload: Record<string, unknown> | null | undefined, key: string) => {
    if (!payload) {
      return undefined;
    }

    const camelKey = key.length > 0 ? `${key[0].toLowerCase()}${key.slice(1)}` : key;

    return payload[key] ?? payload[camelKey];
  };

  const getPayloadString = (payload: Record<string, unknown> | null | undefined, key: string) => {
    const value = getPayloadValue(payload, key);
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  };

  const getPayloadNestedString = (
    payload: Record<string, unknown> | null | undefined,
    parentKey: string,
    childKey: string,
  ) => {
    const parent = getPayloadValue(payload, parentKey);
    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) {
      return null;
    }

    return getPayloadString(parent as Record<string, unknown>, childKey);
  };

  const getPayloadNumber = (payload: Record<string, unknown> | null | undefined, key: string) => {
    const value = getPayloadValue(payload, key);
    return typeof value === 'number' ? value : null;
  };

  const getEventPresentation = (event: TransactionEvent, transaction: Transaction) => {
    const payload = event.payload || undefined;
    const actualCryptoAmount = getReceivedCryptoAmount(transaction) ?? getPayloadNumber(payload, 'ActualCryptoAmount');
    const quotedCryptoAmount = getPayloadNumber(payload, 'QuotedCryptoAmount');
    const cryptoCurrency = getPayloadString(payload, 'CryptoCurrency') || transaction.cryptoCurrency || 'USDC';
    const providerStatus = getPayloadString(payload, 'ProviderStatus') || getPayloadString(payload, 'ExternalStatus');
    const providerFailureMessage = getPayloadNestedString(payload, 'Failure', 'Message');
    const txHash = getPayloadString(payload, 'TxHash');

    switch (event.type) {
      case 'TransactionCreated':
        return {
          title: 'Payment request created',
          description: 'The payment page, smart-contract address, and provider options were created.',
          accent: '#60a5fa',
          border: 'rgba(96, 165, 250, 0.35)',
        };
      case 'QuoteIssued':
        return {
          title: 'Quote issued',
          description: quotedCryptoAmount !== null
            ? `Initial quote set at ${formatTokenAmount(quotedCryptoAmount, cryptoCurrency)}.`
            : 'Initial quote was issued for the direct crypto payment flow.',
          accent: '#38bdf8',
          border: 'rgba(56, 189, 248, 0.35)',
        };
      case 'QuoteRefreshed':
        return {
          title: 'Quote refreshed',
          description: quotedCryptoAmount !== null
            ? `Quote refreshed to ${formatTokenAmount(quotedCryptoAmount, cryptoCurrency)}.`
            : 'The direct crypto quote was refreshed.',
          accent: '#38bdf8',
          border: 'rgba(56, 189, 248, 0.35)',
        };
      case 'ProviderStatusUpdated':
        return {
          title: 'Provider status updated',
          description: providerFailureMessage ||
            (providerStatus
            ? `The selected onramp provider moved to ${providerStatus}.`
            : 'A fiat onramp provider reported a status change.'),
          accent: '#fbbf24',
          border: 'rgba(251, 191, 36, 0.35)',
        };
      case 'PaymentCompleted':
        return {
          title: 'Payment detected',
          description: actualCryptoAmount !== null
            ? `The system detected ${formatTokenAmount(actualCryptoAmount, cryptoCurrency)} for this transaction.`
            : 'A payment was detected for this transaction.',
          accent: '#34d399',
          border: 'rgba(52, 211, 153, 0.35)',
        };
      case 'PartialPaymentReceived':
        return {
          title: 'Underpayment received',
          description: quotedCryptoAmount !== null && actualCryptoAmount !== null
            ? `Quoted ${formatTokenAmount(quotedCryptoAmount, cryptoCurrency)}, received ${formatTokenAmount(actualCryptoAmount, cryptoCurrency)}.`
            : 'The received amount was lower than the quoted amount, but the payment was still accepted.',
          accent: '#f59e0b',
          border: 'rgba(245, 158, 11, 0.35)',
        };
      case 'OverpaymentReceived':
        return {
          title: 'Overpayment received',
          description: quotedCryptoAmount !== null && actualCryptoAmount !== null
            ? `Quoted ${formatTokenAmount(quotedCryptoAmount, cryptoCurrency)}, received ${formatTokenAmount(actualCryptoAmount, cryptoCurrency)}.`
            : 'The received amount was higher than the quoted amount and was accepted.',
          accent: '#a78bfa',
          border: 'rgba(167, 139, 250, 0.35)',
        };
      case 'LateQuotePaymentReceived':
        return {
          title: 'Payment arrived after quote expiry',
          description: 'The payment arrived after the quote timer expired and was still processed normally.',
          accent: '#c084fc',
          border: 'rgba(192, 132, 252, 0.35)',
        };
      case 'PayoutClaimed':
        return {
          title: 'Settlement claimed',
          description: txHash
            ? `The smart-contract payout was claimed on-chain. Tx hash: ${txHash}.`
            : 'The smart-contract payout was claimed on-chain.',
          accent: '#22c55e',
          border: 'rgba(34, 197, 94, 0.35)',
        };
      case 'PayoutCompleted':
        return {
          title: 'Settlement completed',
          description: 'The smart-contract service distributed the payout to all configured parties.',
          accent: '#10b981',
          border: 'rgba(16, 185, 129, 0.35)',
        };
      case 'TransactionCompleted':
        return {
          title: 'Business notifications sent',
          description: 'The transaction completion notifications were sent to the configured integrations.',
          accent: '#60a5fa',
          border: 'rgba(96, 165, 250, 0.35)',
        };
      default:
        return {
          title: event.type,
          description: `Event received from ${event.source}.`,
          accent: '#94a3b8',
          border: 'rgba(148, 163, 184, 0.25)',
        };
    }
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getPrimaryReference = (transaction: Transaction) => {
    return (
      transaction.merchantReferences?.merchantTransactionId ||
      transaction.publicPaymentId ||
      `TX-${transaction.id}`
    );
  };

  const getSecondaryReference = (transaction: Transaction) => {
    if (transaction.merchantReferences?.merchantUserId) {
      return `Merchant user ID: ${transaction.merchantReferences.merchantUserId}`;
    }

    if (transaction.merchantReferences?.idempotencyKey) {
      return `Key: ${transaction.merchantReferences.idempotencyKey}`;
    }

    return null;
  };

  const getUsernameById = (userId: number): string => {
    // For Agent role, users are not fetched, so just show User ID
    if (userRole === 'Agent') {
      return `User ${userId}`;
    }
    
    // If users haven't been loaded yet, show loading state
    if (users.length === 0) {
      return 'Loading...';
    }
    
    // Loop through the full users response from /api/Users
    // Map transaction userId with users response id to display name
    const user = users.find(u => u.id === userId);
    return user ? user.name : `User ${userId}`;
  };

  const getUserById = (userId: number): User | undefined => {
    return users.find(u => u.id === userId);
  };

  const getTransactionAccountIdLabel = (transaction: Transaction): string => {
    const accountUser = getUserById(transaction.userId);

    if (accountUser?.role === 'Agent') {
      return `${SUB_MERCHANT_LABEL} ID`;
    }

    if (accountUser?.role === 'SuperAgent') {
      return `${MERCHANT_LABEL} ID`;
    }

    if (accountUser?.role === 'Admin') {
      return `${PROCESSOR_LABEL} ID`;
    }

    return 'Account ID';
  };

  const getTransactionAccountDisplay = (transaction: Transaction): string =>
    `${getTransactionAccountIdLabel(transaction)} ${transaction.userId}`;

  const getTransactionAccountContext = (transaction: Transaction): string => {
    const accountUser = getUserById(transaction.userId);

    if (!accountUser) {
      return `Account ID ${transaction.userId}`;
    }

    if (accountUser.role === 'Agent') {
      const merchant = getSuperAgentForAgent(accountUser.id);
      return merchant
        ? `${SUB_MERCHANT_LABEL} account - ${MERCHANT_LABEL} ID ${merchant.id}`
        : `${SUB_MERCHANT_LABEL} account`;
    }

    if (accountUser.role === 'SuperAgent') {
      return `${MERCHANT_LABEL} account`;
    }

    if (accountUser.role === 'Admin') {
      return `${PROCESSOR_LABEL} account`;
    }

    return `Account ID ${transaction.userId}`;
  };

  const formatPaymentSourceLabel = (paymentSource?: string | null): string => {
    switch ((paymentSource || '').trim()) {
      case 'FiatOnramp':
        return 'Onramp flow';
      case 'CryptoSwap':
        return 'Swap flow';
      case 'DirectCrypto':
        return 'Direct crypto';
      default:
        return paymentSource?.trim() || 'Direct crypto';
    }
  };

  const getSuperAgentForAgent = (agentId: number): User | undefined => {
    const agent = users.find(u => u.id === agentId);

    if (agent && agent.createdById) {
      const superAgent = users.find(u => u.id === agent.createdById);
      return superAgent;
    }
    return undefined;
  };

  const toggleSection = (section: 'generatedLinks' | 'wallets' | 'merchantReferences' | 'webhookDeliveries' | 'settlements') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const fetchWalletBalances = useCallback(async (addresses: string[]) => {
    setLoadingBalances(true);
    try {
      const balancePromises = addresses.map(async (address) => {
        try {
          const balance = await walletService.getWalletBalance(address);
          return { address, balance };
        } catch (error) {
          console.error(`Error fetching balance for ${address}:`, error);
          return { address, balance: { address, usdcBalance: 0, maticBalance: 0 } };
        }
      });

      const results = await Promise.all(balancePromises);
      const balanceMap: { [address: string]: WalletBalance } = {};
      results.forEach(({ address, balance }) => {
        balanceMap[address] = balance;
      });
      setWalletBalances(balanceMap);
    } catch (error) {
      console.error('Error fetching wallet balances:', error);
    } finally {
      setLoadingBalances(false);
    }
  }, []);

  const fetchTransactionDetails = useCallback(async (transactionId: number): Promise<Transaction | null> => {
    try {
      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}/${transactionId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      return null;
    }
  }, [getAuthHeaders]);

  const fetchTransactionCaseDetails = useCallback(async (transactionId: number): Promise<TransactionCaseDetails | null> => {
    try {
      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}/${transactionId}/case`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (fetchError) {
      console.error('Error fetching transaction case details:', fetchError);
      return null;
    }
  }, [getAuthHeaders]);

  const syncCaseDetailsIntoSelection = useCallback((transaction: Transaction, caseDetails: TransactionCaseDetails | null) => {
    const caseStatus = caseDetails?.caseStatus || transaction.caseStatus || 'Open';
    const nextTransaction = {
      ...transaction,
      caseStatus,
      caseOwnerUserId: caseDetails?.caseOwnerUserId ?? transaction.caseOwnerUserId,
      caseOwnerName: caseDetails?.caseOwnerName ?? transaction.caseOwnerName,
      caseDueAt: caseDetails?.caseDueAt ?? transaction.caseDueAt,
      caseReminderEnabled: caseDetails?.reminderEnabled ?? transaction.caseReminderEnabled,
      caseEscalationEnabled: caseDetails?.escalationEnabled ?? transaction.caseEscalationEnabled,
      caseReminderSentAt: caseDetails?.reminderSentAt ?? transaction.caseReminderSentAt,
      caseEscalatedAt: caseDetails?.escalatedAt ?? transaction.caseEscalatedAt,
    };

    setSelectedTransaction(nextTransaction);
    setSelectedCaseDetails(caseDetails ? {
      ...caseDetails,
      caseStatus,
    } : {
      transactionId: transaction.id,
      caseStatus,
      caseOwnerUserId: transaction.caseOwnerUserId,
      caseOwnerName: transaction.caseOwnerName,
      caseDueAt: transaction.caseDueAt,
      reminderEnabled: transaction.caseReminderEnabled || false,
      escalationEnabled: transaction.caseEscalationEnabled || false,
      reminderSentAt: transaction.caseReminderSentAt,
      escalatedAt: transaction.caseEscalatedAt,
      caseNotes: [],
    });
  }, []);

  const handleRefreshSelectedTransaction = useCallback(async () => {
    if (!selectedTransaction) {
      return;
    }

    const [freshTransaction, freshCaseDetails] = await Promise.all([
      fetchTransactionDetails(selectedTransaction.id),
      fetchTransactionCaseDetails(selectedTransaction.id),
    ]);

    if (freshTransaction) {
      syncCaseDetailsIntoSelection(freshTransaction, freshCaseDetails);
      if (userRole !== 'Agent') {
        await fetchWalletBalances([freshTransaction.uniqueWalletAddress]);
      }
    } else if (freshCaseDetails) {
      syncCaseDetailsIntoSelection(selectedTransaction, freshCaseDetails);
    }
  }, [
    fetchTransactionCaseDetails,
    fetchTransactionDetails,
    fetchWalletBalances,
    selectedTransaction,
    syncCaseDetailsIntoSelection,
    userRole,
  ]);

  const openTransactionModal = useCallback(async (transactionId: number, seedTransaction?: Transaction | null) => {
    const initialTransaction = seedTransaction ?? transactions.find((transaction) => transaction.id === transactionId) ?? null;

    if (initialTransaction) {
      setSelectedTransaction(initialTransaction);
    }

    setShowTransactionModal(true);

    try {
      const [freshTransaction, freshCaseDetails] = await Promise.all([
        fetchTransactionDetails(transactionId),
        fetchTransactionCaseDetails(transactionId),
      ]);

      const nextTransaction = freshTransaction ?? initialTransaction;
      if (nextTransaction) {
        syncCaseDetailsIntoSelection(nextTransaction, freshCaseDetails);
        if (userRole !== 'Agent') {
          await fetchWalletBalances([nextTransaction.uniqueWalletAddress]);
        }
      } else if (freshCaseDetails && initialTransaction) {
        syncCaseDetailsIntoSelection(initialTransaction, freshCaseDetails);
      }
    } catch (modalError) {
      console.error('Failed to open transaction modal', modalError);
      setError(modalError instanceof Error ? modalError.message : 'Failed to open transaction');
    }

    try {
      await appNotificationService.markTransactionNotificationsAsRead(transactionId);
    } catch (markReadError) {
      console.error('Failed to mark transaction notifications as read', markReadError);
    }
  }, [
    fetchTransactionCaseDetails,
    fetchTransactionDetails,
    fetchWalletBalances,
    syncCaseDetailsIntoSelection,
    transactions,
    userRole,
  ]);

  useEffect(() => {
    if (!showTransactionModal || !selectedTransaction) {
      return;
    }

    const handle = window.setInterval(() => {
      void handleRefreshSelectedTransaction();
    }, 15000);

    return () => window.clearInterval(handle);
  }, [handleRefreshSelectedTransaction, selectedTransaction, showTransactionModal]);

  useEffect(() => {
    const openTransactionId = getOpenTransactionIdFromSearch(location.search);
    if (!openTransactionId) {
      lastOpenedFromQueryRef.current = null;
      return;
    }

    if (showTransactionModal && selectedTransaction?.id === openTransactionId) {
      lastOpenedFromQueryRef.current = openTransactionId;
      return;
    }

    if (lastOpenedFromQueryRef.current === openTransactionId) {
      return;
    }

    lastOpenedFromQueryRef.current = openTransactionId;
    void openTransactionModal(openTransactionId);
  }, [location.search, openTransactionModal, selectedTransaction?.id, showTransactionModal]);

  const handleAddCaseNote = useCallback(async () => {
    if (!selectedTransaction || !caseNoteDraft.trim()) {
      return;
    }

    try {
      setCaseActionLoading(true);
      setError(null);

      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}/${selectedTransaction.id}/case-notes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ note: caseNoteDraft.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.expandedError?.message || errorData?.message || 'Failed to add case note');
      }

      setCaseNoteDraft('');
      await handleRefreshSelectedTransaction();
      await fetchTransactions(pagination.currentPage, true);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Failed to add case note');
    } finally {
      setCaseActionLoading(false);
    }
  }, [caseNoteDraft, fetchTransactions, getAuthHeaders, handleRefreshSelectedTransaction, pagination.currentPage, selectedTransaction]);

  const updateCaseWorkflowDraft = <K extends keyof CaseWorkflowDraft>(field: K, value: CaseWorkflowDraft[K]) => {
    setCaseWorkflowDraft((previous) => ({
      ...previous,
      [field]: value,
    }));
    setCaseWorkflowDirty(true);
  };

  const handleUpdateCaseStatus = useCallback(async (nextCaseStatus: string, note?: string) => {
    if (!selectedTransaction) {
      return;
    }

    try {
      setCaseActionLoading(true);
      setError(null);

      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}/${selectedTransaction.id}/case-status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          caseStatus: nextCaseStatus,
          note: note?.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.expandedError?.message || errorData?.message || 'Failed to update case status');
      }

      await handleRefreshSelectedTransaction();
      await fetchTransactions(pagination.currentPage, true);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Failed to update case status');
    } finally {
      setCaseActionLoading(false);
    }
  }, [fetchTransactions, getAuthHeaders, handleRefreshSelectedTransaction, pagination.currentPage, selectedTransaction]);

  const handleUpdateCaseWorkflow = useCallback(async () => {
    if (!selectedTransaction) {
      return;
    }

    try {
      setCaseActionLoading(true);
      setError(null);

      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}/${selectedTransaction.id}/case-workflow`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          caseOwnerUserId: caseWorkflowDraft.caseOwnerUserId ? parseInt(caseWorkflowDraft.caseOwnerUserId, 10) : null,
          caseDueAt: caseWorkflowDraft.caseDueAt ? formatDateForAPI(caseWorkflowDraft.caseDueAt) : null,
          reminderEnabled: caseWorkflowDraft.reminderEnabled,
          escalationEnabled: caseWorkflowDraft.escalationEnabled,
          note: caseWorkflowDraft.note.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.expandedError?.message || errorData?.message || 'Failed to update case workflow');
      }

      setCaseWorkflowDirty(false);
      setCaseWorkflowDraft((previous) => ({ ...previous, note: '' }));
      await handleRefreshSelectedTransaction();
      await fetchTransactions(pagination.currentPage, true);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Failed to update case workflow');
    } finally {
      setCaseActionLoading(false);
    }
  }, [caseWorkflowDraft, fetchTransactions, getAuthHeaders, handleRefreshSelectedTransaction, pagination.currentPage, selectedTransaction]);

  const handleApplyBulkCaseStatus = useCallback(async () => {
    if (selectedTransactionIds.length === 0) {
      return;
    }

    try {
      setBulkActionLoading(true);
      setError(null);

      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}/ops/bulk-update`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          transactionIds: selectedTransactionIds,
          caseStatus: bulkCaseStatus,
          note: bulkCaseNote.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.expandedError?.message || errorData?.message || 'Failed to update selected cases');
      }

      setSelectedTransactionIds([]);
      setBulkCaseNote('');
      setLiveToast({
        title: 'Cases updated',
        message: `${selectedTransactionIds.length} transaction case${selectedTransactionIds.length === 1 ? '' : 's'} marked as ${getCaseStatusLabel(bulkCaseStatus)}.`,
      });
      await fetchTransactions(pagination.currentPage, true);

      if (selectedTransaction) {
        await handleRefreshSelectedTransaction();
      }
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Failed to update selected cases');
    } finally {
      setBulkActionLoading(false);
    }
  }, [bulkCaseNote, bulkCaseStatus, fetchTransactions, getAuthHeaders, handleRefreshSelectedTransaction, pagination.currentPage, selectedTransaction, selectedTransactionIds]);

  const handleTransactionClick = async (transaction: Transaction) => {
    lastOpenedFromQueryRef.current = transaction.id;
    syncFiltersToUrl(filters, transaction.id, false);
    await openTransactionModal(transaction.id, transaction);
  };

  const handleCloseModal = () => {
    const returnTo = getSafeReturnToFromSearch(location.search);
    setShowTransactionModal(false);
    setSelectedTransaction(null);
    setSelectedCaseDetails(null);
    setCaseNoteDraft('');
    setCaseWorkflowDraft({
      transactionId: null,
      caseOwnerUserId: '',
      caseDueAt: '',
      reminderEnabled: false,
      escalationEnabled: false,
      note: '',
    });
    setCaseWorkflowDirty(false);

    cleanupModalArtifacts();

    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }

    if (location.pathname === '/transactions') {
      syncFiltersToUrl(filters, null, false);
    }
  };

  useEffect(() => {
    return () => {
      cleanupModalArtifacts();
    };
  }, []);

  const handleToggleTransactionSelection = (transactionId: number) => {
    setSelectedTransactionIds((previous) =>
      previous.includes(transactionId)
        ? previous.filter((value) => value !== transactionId)
        : [...previous, transactionId]);
  };

  const handleToggleVisibleTransactionSelection = () => {
    const visibleIds = transactions.map((transaction) => transaction.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((transactionId) => selectedTransactionIds.includes(transactionId));

    setSelectedTransactionIds(allSelected
      ? selectedTransactionIds.filter((transactionId) => !visibleIds.includes(transactionId))
      : Array.from(new Set([...selectedTransactionIds, ...visibleIds])));
  };

  const renderPagination = () => {
    // Don't show pagination if there's only one page or no results
    if (pagination.totalPages <= 1) {
      return null;
    }

    const pages = [];
    const startPage = Math.max(1, pagination.currentPage - 2);
    const endPage = Math.min(pagination.totalPages, pagination.currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === pagination.currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="app-pagination app-pagination--operator">
        <Pagination.First 
          onClick={() => handlePageChange(1)}
          disabled={pagination.currentPage === 1}
        />
        <Pagination.Prev 
          onClick={() => handlePageChange(pagination.currentPage - 1)}
          disabled={pagination.currentPage === 1}
        />
        {pages}
        <Pagination.Next 
          onClick={() => handlePageChange(pagination.currentPage + 1)}
          disabled={pagination.currentPage === pagination.totalPages}
        />
        <Pagination.Last 
          onClick={() => handlePageChange(pagination.totalPages)}
          disabled={pagination.currentPage === pagination.totalPages}
        />
      </Pagination>
    );
  };

  const selectedVisibleCount = transactions.filter((transaction) => selectedTransactionIds.includes(transaction.id)).length;
  const settledCount = transactions.filter((transaction) => isSettledStatus(transaction.status)).length;
  const needsAttentionCount = transactions.filter((transaction) => {
    const dueState = getCaseDueState(transaction.caseDueAt);
    const displayLabel = (transaction.displayStatus || '').toLowerCase();

    return !isCaseResolved(transaction.caseStatus) && (
      Boolean(transaction.caseEscalatedAt) ||
      Boolean(transaction.caseReminderSentAt) ||
      Boolean(dueState?.overdue) ||
      transaction.status === 'Failed' ||
      transaction.status === 'Expired' ||
      displayLabel.includes('underpaid') ||
      displayLabel.includes('overpaid')
    );
  }).length;
  const activeFilterCount = Object.values(filters).filter((value) => value.trim().length > 0).length;
  const advancedFilterCount = [filters.dateFrom, filters.dateTo, filters.caseStatus, filters.workflowState, filters.caseOwnerUserId]
    .filter((value) => value.trim().length > 0)
    .length;
  const selectedModalCaseStatus = selectedCaseDetails?.caseStatus || selectedTransaction?.caseStatus;
  const selectedModalDueState = getCaseDueState(selectedCaseDetails?.caseDueAt || selectedTransaction?.caseDueAt || null);

  const renderExpandableSectionHeader = (
    section: 'generatedLinks' | 'wallets' | 'merchantReferences' | 'webhookDeliveries' | 'settlements',
    title: string,
    subtitle: string,
  ) => (
    <button
      type="button"
      className="transaction-detail-modal__section-toggle"
      onClick={() => toggleSection(section)}
    >
      <div>
        <div className="transaction-detail-modal__section-title">{title}</div>
        <div className="transaction-detail-modal__section-subtitle">{subtitle}</div>
      </div>
      <span className="transaction-detail-modal__toggle-label">
        {expandedSections[section] ? 'Hide' : 'Show'}
      </span>
    </button>
  );

  if (loading && transactions.length === 0) {
    return (
      <div className="app-shell operator-page transaction-workspace">
        <Container className="operator-page__container">
          <div className="text-center">
            <Spinner animation="border" role="status" className="app-spinner">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="app-shell operator-page transaction-workspace">
      <Container className="operator-page__container">
        <ToastContainer position="top-end" className="p-3">
          <Toast bg="primary" onClose={() => setLiveToast(null)} show={!!liveToast} delay={6000} autohide>
            <Toast.Header closeButton>
              <strong className="me-auto">{liveToast?.title}</strong>
            </Toast.Header>
            <Toast.Body className="text-white">{liveToast?.message}</Toast.Body>
          </Toast>
        </ToastContainer>
        <InternalPageHeader
          eyebrow="Payment operations"
          title="Transactions"
          description="Find the payments that matter, open the right exceptions fast, and move review work forward."
          actions={(
            <>
              {browserAlertsSupported() && notificationPermission !== 'granted' && (
                <Button variant="outline-info" size="sm" onClick={() => void handleEnableBrowserAlerts()}>
                  Enable browser alerts
                </Button>
              )}
              <Button variant="outline-primary" size="sm" onClick={() => void handleExportCsv()}>
                Export filtered CSV
              </Button>
              <Button variant={paymentToneEnabled ? 'outline-success' : 'outline-secondary'} size="sm" onClick={handleTogglePaymentTone}>
                {paymentToneEnabled ? 'Payment tone on' : 'Payment tone off'}
              </Button>
            </>
          )}
          meta={[
            {
              label: 'Refresh',
              value: lastRefreshedAt ? `Last checked ${new Date(lastRefreshedAt).toLocaleTimeString()}` : 'Every 15 seconds',
            },
            { label: 'Matching payments', value: pagination.totalItems },
            {
              label: 'Filters',
              value: activeFilterCount === 0 ? 'Showing everything' : `${activeFilterCount} active`,
            },
          ]}
        />

        <div className="operator-summary-grid mb-4">
          <div className="operator-summary-card" style={{ '--summary-accent': 'var(--app-blue)' } as React.CSSProperties}>
            <div className="operator-summary-card__label">This Page</div>
            <div className="operator-summary-card__value">{transactions.length}</div>
            <div className="operator-summary-card__note">Payments loaded in the current view.</div>
          </div>
          <div className="operator-summary-card" style={{ '--summary-accent': '#fbbf24' } as React.CSSProperties}>
            <div className="operator-summary-card__label">Needs Attention</div>
            <div className="operator-summary-card__value">{needsAttentionCount}</div>
            <div className="operator-summary-card__note">Exceptions, overdue follow-up, or escalated reviews.</div>
          </div>
          <div className="operator-summary-card" style={{ '--summary-accent': '#7dd3a4' } as React.CSSProperties}>
            <div className="operator-summary-card__label">Settled</div>
            <div className="operator-summary-card__value">{settledCount}</div>
            <div className="operator-summary-card__note">Payments already completed or fully settled.</div>
          </div>
          <div className="operator-summary-card" style={{ '--summary-accent': '#93c5fd' } as React.CSSProperties}>
            <div className="operator-summary-card__label">Selected</div>
            <div className="operator-summary-card__value">{selectedVisibleCount}</div>
            <div className="operator-summary-card__note">Ready for bulk review updates from this page.</div>
          </div>
        </div>

        <Card className="operator-card mb-4 transactions-page__filters-card">
          <Card.Header className="operator-card__header d-flex justify-content-between align-items-center gap-3 flex-wrap">
            <div>
              <h5 className="operator-card__title">Find the payments you need</h5>
              <div className="operator-card__subtitle">
                Search first, then add only the filters you need.
              </div>
            </div>
            <div className="operator-muted-text">
              {activeFilterCount === 0 ? 'No filters applied' : `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied`}
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="g-3 align-items-end transaction-filters__primary-row">
              <Col lg={2} md={6} className="d-flex">
                <Form.Group className="operator-filter-group">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="operator-control"
                  >
                    <option value="">All payment stages</option>
                    <option value="Issued">Issued</option>
                    <option value="Completed">Completed</option>
                    <option value="PayoutCompleted">Settled</option>
                    <option value="Pending">Pending</option>
                    <option value="Failed">Failed</option>
                  </Form.Select>
                  <Form.Text>Use the main payment stage to narrow the queue quickly.</Form.Text>
                </Form.Group>
              </Col>
              <Col lg={3} md={6} className="d-flex">
                <Form.Group className="operator-filter-group">
                  <Form.Label>Needs attention</Form.Label>
                  <Form.Select
                    value={filters.exceptionType}
                    onChange={(e) => handleFilterChange('exceptionType', e.target.value)}
                    className="operator-control"
                  >
                    <option value="">All payments</option>
                    <option value="any">Any issue</option>
                    <option value="underpaid">Underpaid</option>
                    <option value="overpaid">Overpaid</option>
                    <option value="late_quote">Late quote</option>
                    <option value="failed">Failed</option>
                    <option value="expired">Expired</option>
                    <option value="webhook_retrying">Delivery retrying</option>
                    <option value="webhook_failed">Delivery failed</option>
                  </Form.Select>
                  <Form.Text>Focus on payments that may need a manual check.</Form.Text>
                </Form.Group>
              </Col>
              <Col lg={(userRole === 'Admin' || userRole === 'SuperAgent') ? 4 : 7} md={12} className="d-flex">
                <Form.Group className="operator-filter-group operator-filter-group--search">
                  <Form.Label>Search references</Form.Label>
                  <Form.Control
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Reference ID, wallet, payment page, transaction hash, or payment number"
                    className="operator-control"
                  />
                  <Form.Text>Search IDs, wallets, pages, and chain records.</Form.Text>
                </Form.Group>
              </Col>
              {(userRole === 'Admin' || userRole === 'SuperAgent') && (
                <Col lg={3} md={6} className="d-flex">
                  <Form.Group className="operator-filter-group">
                    <Form.Label>Account</Form.Label>
                    <div className={`operator-search-surface ${showUserDropdown ? 'operator-search-surface--open' : ''}`} ref={userDropdownRef}>
                      <Form.Control
                        type="text"
                        placeholder="Find an account"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setShowUserDropdown(true);
                        }}
                        onFocus={() => setShowUserDropdown(true)}
                        className="operator-control"
                      />
                      <button
                        type="button"
                        className="operator-search-toggle"
                        onClick={() => setShowUserDropdown((previous) => !previous)}
                        aria-label={showUserDropdown ? 'Hide account list' : 'Show account list'}
                      >
                        <span className="operator-toggle-indicator">{showUserDropdown ? 'Hide' : 'Show'}</span>
                      </button>

                      {showUserDropdown && (
                        <div className="operator-search-menu">
                          <button
                            type="button"
                            className={`operator-search-option ${filters.userId === '' ? 'operator-search-option--active' : ''}`}
                            onClick={() => handleUserSelect('')}
                          >
                            All accounts
                            <span className="operator-search-option__meta">Show every business account in this queue.</span>
                          </button>
                          {getFilteredUsers().map((user) => (
                            <button
                              type="button"
                              key={user.id}
                              className={`operator-search-option ${filters.userId === user.id.toString() ? 'operator-search-option--active' : ''}`}
                              onClick={() => handleUserSelect(user.id.toString())}
                            >
                              {user.name} ({getRoleDisplayLabel(user.role)})
                              <span className="operator-search-option__meta">{user.email}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {filters.userId && (
                      <div className="operator-inline-note mt-2">
                        Selected account: {getSelectedUserDisplayLabel()}
                      </div>
                    )}
                    <Form.Text className="operator-filter-group__hint">
                      Scope the queue to one account when you want a cleaner review list.
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>

            <div className="transaction-filters__toolbar mt-4">
              <div>
                <Button variant="primary" onClick={handleApplyFilters}>
                  Apply filters
                </Button>
                <Button variant="outline-secondary" className="ms-2" onClick={handleClearFilters}>
                  Clear
                </Button>
              </div>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <Button
                  variant="outline-light"
                  className="transaction-filters__advanced-button"
                  onClick={() => setShowAdvancedFilters((previous) => !previous)}
                >
                  {showAdvancedFilters ? 'Hide advanced filters' : 'Show advanced filters'}
                </Button>
                <span className="operator-inline-note">
                  {advancedFilterCount === 0
                    ? 'Advanced filters cover date range, review state, follow-up, and owner.'
                    : `${advancedFilterCount} advanced filter${advancedFilterCount === 1 ? '' : 's'} active.`}
                </span>
              </div>
            </div>

            {showAdvancedFilters && (
              <div className="transaction-filters__advanced mt-4">
                <Row className="g-3">
                  <Col xl={2} md={6}>
                    <Form.Group>
                      <Form.Label>From</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className="operator-control"
                      />
                    </Form.Group>
                  </Col>
                  <Col xl={2} md={6}>
                    <Form.Group>
                      <Form.Label>To</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className="operator-control"
                      />
                    </Form.Group>
                  </Col>
                  <Col xl={3} md={6}>
                    <Form.Group>
                      <Form.Label>Review state</Form.Label>
                      <Form.Select
                        value={filters.caseStatus}
                        onChange={(e) => handleFilterChange('caseStatus', e.target.value)}
                        className="operator-control"
                      >
                        <option value="">Any review state</option>
                        <option value="Unresolved">Needs review</option>
                        <option value="Open">Open</option>
                        <option value="InReview">In review</option>
                        <option value="Resolved">Resolved</option>
                      </Form.Select>
                      <Form.Text>Track whether a payment still needs review or is already resolved.</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xl={3} md={6}>
                    <Form.Group>
                      <Form.Label>Follow-up</Form.Label>
                      <Form.Select
                        value={filters.workflowState}
                        onChange={(e) => handleFilterChange('workflowState', e.target.value)}
                        className="operator-control"
                      >
                        <option value="">Any follow-up state</option>
                        <option value="overdue">Overdue</option>
                        <option value="reminder_enabled">Reminder enabled</option>
                        <option value="escalation_enabled">Escalation enabled</option>
                        <option value="reminder_due">Reminder due</option>
                        <option value="escalation_due">Escalation due</option>
                        <option value="escalated">Escalated</option>
                      </Form.Select>
                      <Form.Text>Keep the queue focused on what needs a response next.</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xl={2} md={6}>
                    <Form.Group>
                      <Form.Label>Owner</Form.Label>
                      <Form.Select
                        value={filters.caseOwnerUserId}
                        onChange={(e) => handleFilterChange('caseOwnerUserId', e.target.value)}
                        className="operator-control"
                      >
                        <option value="">All owners</option>
                        <option value="0">Unassigned</option>
                        {getCaseOwnerOptions().map((owner) => (
                          <option key={owner.id} value={owner.id.toString()}>
                            {owner.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text>Filter by teammate ownership, or show items that still need an owner.</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <div className="settings-callout mt-3">
                  Use owner and follow-up together when you want a clean action queue instead of scanning the whole table.
                </div>
              </div>
            )}
          </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

        {selectedTransactionIds.length > 0 && (
          <Card className="operator-card mb-4">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={3}>
                  <div className="operator-card__title">
                    {selectedTransactionIds.length} selected for bulk review
                  </div>
                  <div className="operator-card__subtitle mt-1">
                    Update the review state for selected payments in one pass, with an optional note for your team.
                  </div>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>New review state</Form.Label>
                    <Form.Select
                      value={bulkCaseStatus}
                      onChange={(e) => setBulkCaseStatus(e.target.value)}
                      className="operator-control"
                    >
                      <option value="Open">Open</option>
                      <option value="InReview">In review</option>
                      <option value="Resolved">Resolved</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Team note</Form.Label>
                    <Form.Control
                      type="text"
                      value={bulkCaseNote}
                      onChange={(e) => setBulkCaseNote(e.target.value)}
                      placeholder="Optional note to explain the update"
                      className="operator-control"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <div className="d-grid gap-2">
                    <Button variant="primary" onClick={() => void handleApplyBulkCaseStatus()} disabled={bulkActionLoading}>
                      {bulkActionLoading ? 'Saving...' : 'Apply update'}
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setSelectedTransactionIds([])} disabled={bulkActionLoading}>
                      Clear selection
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        <Card className="operator-card transactions-page__queue-card">
          <Card.Header className="operator-card__header d-flex justify-content-between align-items-center gap-3 flex-wrap">
            <div>
              <h5 className="operator-card__title">Activity queue</h5>
              <div className="operator-card__subtitle">
                Open any payment for its activity, review notes, and payout details.
              </div>
            </div>
            <span className="operator-muted-text">
              {pagination.totalItems} matching payments
            </span>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="transaction-workspace__desktop-table">
              <Table
                responsive
                hover
                className={`app-data-table transaction-workspace__table ${userRole === 'Admin' ? 'transaction-workspace__table--admin' : ''}`}
              >
                <thead>
                  <tr>
                    <th className="transaction-workspace__selection-col">
                      <Form.Check
                        type="checkbox"
                        checked={transactions.length > 0 && transactions.every((transaction) => selectedTransactionIds.includes(transaction.id))}
                        onChange={handleToggleVisibleTransactionSelection}
                        onClick={(event) => event.stopPropagation()}
                      />
                    </th>
                    <th>ID</th>
                    <th>Account</th>
                    <th>Reference</th>
                    {userRole === 'Admin' && (
                      <>
                        <th>Provider</th>
                        <th>Funding wallet</th>
                        <th>Fee split</th>
                      </>
                    )}
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Review</th>
                    <th>Payout wallet</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => {
                    const rowColors = getTransactionRowColors(transaction);
                    const dueState = getCaseDueState(transaction.caseDueAt);

                    return (
                      <tr
                        key={transaction.id}
                        className="transaction-workspace__table-row"
                        style={{
                          ['--transaction-row-bg' as string]: rowColors.base,
                          ['--transaction-row-hover' as string]: rowColors.hover,
                        }}
                        onClick={() => handleTransactionClick(transaction)}
                      >
                        <td className="transaction-workspace__selection-col">
                          <Form.Check
                            type="checkbox"
                            checked={selectedTransactionIds.includes(transaction.id)}
                            onChange={() => handleToggleTransactionSelection(transaction.id)}
                            onClick={(event) => event.stopPropagation()}
                          />
                        </td>
                        <td>
                          <div className="app-data-table__primary">#{transaction.id}</div>
                        </td>
                        <td>
                          <div className="app-data-table__primary">{getTransactionAccountDisplay(transaction)}</div>
                          <div className="app-data-table__secondary">{getTransactionAccountContext(transaction)}</div>
                        </td>
                        <td>
                          <div className="app-data-table__primary">{getPrimaryReference(transaction)}</div>
                          {getSecondaryReference(transaction) && (
                            <div className="app-data-table__secondary">
                              {getSecondaryReference(transaction)}
                            </div>
                          )}
                        </td>
                        {userRole === 'Admin' && (
                          <>
                            <td>
                              <div className="app-data-table__primary">{transaction.provider || 'Direct crypto'}</div>
                              {transaction.paymentSource && (
                                <div className="app-data-table__secondary">Path: {formatPaymentSourceLabel(transaction.paymentSource)}</div>
                              )}
                            </td>
                            <td title={transaction.uniqueWalletAddress}>
                              <span className="settings-mono-value">{truncateAddress(transaction.uniqueWalletAddress)}</span>
                            </td>
                            <td>
                              <div className="app-data-table__secondary">{MERCHANT_LABEL}: {transaction.fees.superAgentPercent}%</div>
                              <div className="app-data-table__secondary">{SUB_MERCHANT_LABEL}: {transaction.fees.agentPercent}%</div>
                            </td>
                          </>
                        )}
                        <td>
                          <div className="app-data-table__secondary">Requested</div>
                          <div className="app-data-table__primary">{formatAmount(transaction.fiatAmount, transaction.fiatCurrency)}</div>
                          {isSettledStatus(transaction.status) && getReceivedCryptoAmount(transaction) !== null && (
                            <div className="app-data-table__secondary">
                              Received {formatTokenAmount(getReceivedCryptoAmount(transaction), transaction.cryptoCurrency)}
                            </div>
                          )}
                          {hasSettlementAmountMismatch(transaction) && getDistributedCryptoAmount(transaction) !== null && (
                            <div className="app-data-table__secondary transaction-workspace__warning-text">
                              Distributed {formatTokenAmount(getDistributedCryptoAmount(transaction), transaction.cryptoCurrency)}
                            </div>
                          )}
                        </td>
                        <td>{getStatusBadge(transaction.status, transaction.displayStatus)}</td>
                        <td className="transaction-workspace__review-cell">
                          <div>{getCaseStatusBadge(transaction.caseStatus)}</div>
                          {transaction.caseOwnerName && (
                            <div className="app-data-table__secondary">Owner: {transaction.caseOwnerName}</div>
                          )}
                          {dueState?.label && (
                            <div className={`app-data-table__secondary ${dueState.overdue ? 'transaction-workspace__danger-text' : 'transaction-workspace__info-text'}`}>
                              {dueState.label}
                            </div>
                          )}
                          {(transaction.caseReminderEnabled || transaction.caseEscalationEnabled || transaction.caseReminderSentAt || transaction.caseEscalatedAt) && (
                            <div className="transaction-workspace__case-tags">
                              {transaction.caseReminderEnabled && <Badge bg="info">Reminder enabled</Badge>}
                              {transaction.caseReminderSentAt && <Badge bg="secondary">Reminder sent</Badge>}
                              {transaction.caseEscalationEnabled && <Badge bg="danger">Escalation enabled</Badge>}
                              {transaction.caseEscalatedAt && <Badge bg="danger">Escalated</Badge>}
                            </div>
                          )}
                        </td>
                        <td title={transaction.userWallet}>
                          <span className="settings-mono-value">{truncateAddress(transaction.userWallet)}</span>
                        </td>
                        <td title={formatDateTime(transaction.timestamp)}>
                          <div className="app-data-table__primary">{formatDate(transaction.timestamp)}</div>
                          <div className="app-data-table__secondary">{new Date(transaction.timestamp).toLocaleTimeString()}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <div className="transaction-workspace__mobile-cards">
              {transactions.map((transaction) => {
                const dueState = getCaseDueState(transaction.caseDueAt);
                const isSelected = selectedTransactionIds.includes(transaction.id);

                return (
                  <Card
                    key={`mobile-${transaction.id}`}
                    className={`transaction-workspace__transaction-card ${isSelected ? 'transaction-workspace__transaction-card--selected' : ''}`}
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    <Card.Body>
                      <div className="transaction-workspace__transaction-card-top">
                        <div className="d-flex align-items-start gap-2">
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleTransactionSelection(transaction.id)}
                            onClick={(event) => event.stopPropagation()}
                          />
                          <div>
                            <div className="app-data-table__primary">{getPrimaryReference(transaction)}</div>
                            <div className="app-data-table__secondary">
                              {getSecondaryReference(transaction) || `Transaction #${transaction.id}`}
                            </div>
                          </div>
                        </div>
                        {getStatusBadge(transaction.status, transaction.displayStatus)}
                      </div>
                      <div className="transaction-workspace__card-grid">
                        <div className="transaction-workspace__card-detail">
                          <div className="transaction-workspace__card-label">Account</div>
                          <div className="transaction-workspace__card-value">{getTransactionAccountDisplay(transaction)}</div>
                          <div className="app-data-table__secondary">{getTransactionAccountContext(transaction)}</div>
                        </div>
                        <div className="transaction-workspace__card-detail">
                          <div className="transaction-workspace__card-label">Amount</div>
                          <div className="transaction-workspace__card-value">
                            {formatAmount(transaction.fiatAmount, transaction.fiatCurrency)}
                          </div>
                          {isSettledStatus(transaction.status) && getReceivedCryptoAmount(transaction) !== null && (
                            <div className="app-data-table__secondary">
                              Received {formatTokenAmount(getReceivedCryptoAmount(transaction), transaction.cryptoCurrency)}
                            </div>
                          )}
                          {hasSettlementAmountMismatch(transaction) && getDistributedCryptoAmount(transaction) !== null && (
                            <div className="app-data-table__secondary" style={{ color: '#fbbf24' }}>
                              Distributed {formatTokenAmount(getDistributedCryptoAmount(transaction), transaction.cryptoCurrency)}
                            </div>
                          )}
                        </div>
                        <div className="transaction-workspace__card-detail">
                          <div className="transaction-workspace__card-label">Review</div>
                          <div>{getCaseStatusBadge(transaction.caseStatus)}</div>
                          {transaction.caseOwnerName && (
                            <div className="app-data-table__secondary mt-2">Owner: {transaction.caseOwnerName}</div>
                          )}
                          {dueState?.label && (
                            <div className="app-data-table__secondary" style={{ color: dueState.overdue ? '#fca5a5' : '#93c5fd' }}>
                              {dueState.label}
                            </div>
                          )}
                        </div>
                        <div className="transaction-workspace__card-detail">
                          <div className="transaction-workspace__card-label">Updated</div>
                          <div className="transaction-workspace__card-value">{formatDate(transaction.timestamp)}</div>
                          <div className="app-data-table__secondary">{new Date(transaction.timestamp).toLocaleTimeString()}</div>
                        </div>
                      </div>
                      <div className="transaction-workspace__card-footer">
                        <span className="operator-muted-text">
                          {`Path: ${formatPaymentSourceLabel(transaction.paymentSource)}`}
                          {transaction.provider ? ` - Provider: ${transaction.provider}` : ''}
                        </span>
                        {userRole === 'Admin' && (
                          <span className="settings-mono-value">{truncateAddress(transaction.uniqueWalletAddress)}</span>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
            
            {transactions.length === 0 && !loading && (
              <div className="transaction-workspace__empty">
                No payments matched these filters. Try clearing one or two filters and search again.
              </div>
            )}
          </Card.Body>
          {pagination.totalPages > 1 && (
            <Card.Footer className="transaction-workspace__pagination">
              {renderPagination()}
            </Card.Footer>
          )}
        </Card>
      </Container>

      {/* Transaction Details Modal */}
      <Modal 
        show={showTransactionModal} 
        onHide={handleCloseModal}
        onExited={cleanupModalArtifacts}
        animation={false}
        size="xl"
        centered
        dialogClassName="transaction-detail-modal__dialog"
        contentClassName="operator-modal transaction-detail-modal"
      >
        <Modal.Header closeButton className="align-items-start">
          <div>
            <div className="operator-page__eyebrow mb-2">Payment Review</div>
            <Modal.Title>Payment #{selectedTransaction?.id}</Modal.Title>
            <div className="operator-card__subtitle mt-2">
              Review the payment, follow-up, and payout details in one place.
            </div>
          </div>
          {selectedTransaction && (
            <div className="transaction-detail-modal__header-actions">
              {getStatusBadge(selectedTransaction.status, selectedTransaction.displayStatus)}
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => void handleRefreshSelectedTransaction()}
              >
                Refresh
              </Button>
            </div>
          )}
        </Modal.Header>
        <Modal.Body className="transaction-detail-modal__body">
          {selectedTransaction && (
            <div>
              <div className="operator-summary-grid mb-4">
                <div className="operator-summary-card" style={{ '--summary-accent': 'var(--app-blue)' } as React.CSSProperties}>
                  <div className="operator-summary-card__label">Requested</div>
                  <div className="operator-summary-card__value">
                    {formatAmount(selectedTransaction.fiatAmount, selectedTransaction.fiatCurrency)}
                  </div>
                  <div className="operator-summary-card__note">
                    {selectedTransaction.cryptoCurrency
                      ? `Crypto option: ${selectedTransaction.cryptoCurrency}.`
                      : 'Ready for review.'}
                  </div>
                </div>
                <div className="operator-summary-card" style={{ '--summary-accent': '#7dd3a4' } as React.CSSProperties}>
                  <div className="operator-summary-card__label">Payment Route</div>
                  <div className="operator-summary-card__value transaction-detail-modal__summary-value">
                    {formatPaymentSourceLabel(selectedTransaction.paymentSource || selectedTransaction.provider)}
                  </div>
                  <div className="operator-summary-card__note">
                    {userRole === 'Admin'
                      ? `Provider: ${selectedTransaction.provider || 'Not set'}`
                      : `Status updated ${formatDateTime(selectedTransaction.timestamp)}.`}
                  </div>
                </div>
                <div className="operator-summary-card" style={{ '--summary-accent': '#93c5fd' } as React.CSSProperties}>
                  <div className="operator-summary-card__label">Account</div>
                  <div className="operator-summary-card__value transaction-detail-modal__summary-value">
                    {getTransactionAccountDisplay(selectedTransaction)}
                  </div>
                  <div className="operator-summary-card__note">
                    {getTransactionAccountContext(selectedTransaction)}
                  </div>
                </div>
                <div className="operator-summary-card" style={{ '--summary-accent': '#fbbf24' } as React.CSSProperties}>
                  <div className="operator-summary-card__label">Review State</div>
                  <div className="operator-summary-card__value transaction-detail-modal__summary-status">
                    {getCaseStatusBadge(selectedModalCaseStatus)}
                  </div>
                  <div className="operator-summary-card__note">
                      {selectedModalDueState?.label || 'No review deadline set.'}
                  </div>
                </div>
              </div>

              {isSettledStatus(selectedTransaction.status) && (
                <div className="transaction-detail-modal__amount-strip mb-4">
                  {getReceivedCryptoAmount(selectedTransaction) !== null && (
                    <div className="transaction-detail-modal__amount-chip">
                      <span className="transaction-detail-modal__amount-label">Received</span>
                      <span className="transaction-detail-modal__amount-value">
                        {formatTokenAmount(getReceivedCryptoAmount(selectedTransaction), selectedTransaction.cryptoCurrency)}
                      </span>
                    </div>
                  )}
                  {getDistributedCryptoAmount(selectedTransaction) !== null && (
                    <div className={`transaction-detail-modal__amount-chip ${hasSettlementAmountMismatch(selectedTransaction) ? 'transaction-detail-modal__amount-chip--warning' : ''}`}>
                      <span className="transaction-detail-modal__amount-label">Distributed</span>
                      <span className="transaction-detail-modal__amount-value">
                        {formatTokenAmount(getDistributedCryptoAmount(selectedTransaction), selectedTransaction.cryptoCurrency)}
                      </span>
                    </div>
                  )}
                  <div className="transaction-detail-modal__amount-chip">
                    <span className="transaction-detail-modal__amount-label">Last updated</span>
                    <span className="transaction-detail-modal__amount-value">{formatDateTime(selectedTransaction.timestamp)}</span>
                  </div>
                </div>
              )}

              {hasSettlementAmountMismatch(selectedTransaction) && (
                <Alert variant="warning" className="settings-callout settings-callout--warning border-0">
                  Settlement mismatch: received {formatTokenAmount(getReceivedCryptoAmount(selectedTransaction), selectedTransaction.cryptoCurrency)} but distributed {formatTokenAmount(getDistributedCryptoAmount(selectedTransaction), selectedTransaction.cryptoCurrency)}.
                </Alert>
              )}

              <Row className="mb-3">
                <Col md={12}>
                    <div className="transaction-detail-modal__section-heading">Payment activity</div>
                  <div className="transaction-detail-modal__surface">
                    {(selectedTransaction.events && selectedTransaction.events.length > 0) ? (
                      <div className="transaction-detail-modal__timeline-list">
                        {[...(selectedTransaction.events || [])]
                          .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                          .map((event) => {
                            const presentation = getEventPresentation(event, selectedTransaction);
                            return (
                              <div
                                key={event.id}
                                className="transaction-detail-modal__timeline-item"
                              >
                                <div className="transaction-detail-modal__timeline-dot-wrap">
                                  <div
                                    className="transaction-detail-modal__timeline-dot"
                                    style={{
                                      ['--timeline-accent' as string]: presentation.accent,
                                      ['--timeline-ring' as string]: presentation.border,
                                    }}
                                  />
                                </div>
                                <div
                                  className="transaction-detail-modal__timeline-card"
                                  style={{
                                    ['--timeline-ring' as string]: presentation.border,
                                  }}
                                >
                                  <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                                    <div>
                                      <div className="transaction-detail-modal__timeline-title">{presentation.title}</div>
                                      <div className="transaction-detail-modal__timeline-copy mt-1">{presentation.description}</div>
                                    </div>
                                    <div className="transaction-detail-modal__timeline-meta" style={{ whiteSpace: 'nowrap' }}>
                                      {formatDateTime(event.createdAt)}
                                    </div>
                                  </div>
                                  <div className="transaction-detail-modal__timeline-meta mt-2">
                                    Source: {event.source}
                                    {event.checkoutSessionId ? ` | Session: ${event.checkoutSessionId}` : ''}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="transaction-detail-modal__muted">
                        No activity recorded yet.
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              
              {selectedTransaction.merchantReferences && (
                <Row className="mb-3">
                  <Col md={12}>
                    {renderExpandableSectionHeader(
                      'merchantReferences',
                      'References',
                      'Merchant, session, and request IDs.',
                    )}
                    {expandedSections.merchantReferences && (
                      <div className="transaction-detail-modal__surface mt-3">
                        <div className="transaction-detail-modal__meta-grid">
                          {selectedTransaction.merchantReferences.merchantTransactionId && (
                            <div className="transaction-detail-modal__meta-item">
                              <span className="transaction-detail-modal__meta-label">Business transaction ID</span>
                              <strong>{selectedTransaction.merchantReferences.merchantTransactionId}</strong>
                            </div>
                          )}
                          {selectedTransaction.merchantReferences.merchantUserId && (
                            <div className="transaction-detail-modal__meta-item">
                              <span className="transaction-detail-modal__meta-label">Merchant user ID</span>
                              <strong>{selectedTransaction.merchantReferences.merchantUserId}</strong>
                            </div>
                          )}
                          {selectedTransaction.merchantReferences.merchantSessionId && (
                            <div className="transaction-detail-modal__meta-item">
                              <span className="transaction-detail-modal__meta-label">Session ID</span>
                              <strong>{selectedTransaction.merchantReferences.merchantSessionId}</strong>
                            </div>
                          )}
                          {selectedTransaction.merchantReferences.idempotencyKey && (
                            <div className="transaction-detail-modal__meta-item">
                              <span className="transaction-detail-modal__meta-label">Request key</span>
                              <strong className="transaction-detail-modal__mono">{selectedTransaction.merchantReferences.idempotencyKey}</strong>
                            </div>
                          )}
                        </div>
                        {selectedTransaction.merchantReferences.metadata && Object.keys(selectedTransaction.merchantReferences.metadata).length > 0 && (
                          <div className="transaction-detail-modal__table-surface">
                            <span className="transaction-detail-modal__meta-label">Extra fields</span>
                            <div className="transaction-detail-modal__history-list mt-2">
                              {Object.entries(selectedTransaction.merchantReferences.metadata).map(([key, value]) => (
                                <div key={key} className="transaction-detail-modal__history-item">
                                  <div className="transaction-detail-modal__history-title">{key}</div>
                                  <div className="transaction-detail-modal__history-body transaction-detail-modal__mono mt-2">
                                    {value ?? 'null'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Col>
                </Row>
              )}

              <Row className="mb-3">
                <Col md={12}>
                    <div className="transaction-detail-modal__section-heading">Review</div>
                  <div className="transaction-detail-modal__surface">
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                      <div>
                        <div className="transaction-detail-modal__meta-label">Current review state</div>
                        <div style={{ marginTop: '4px' }}>
                          {getCaseStatusBadge(selectedCaseDetails?.caseStatus || selectedTransaction.caseStatus)}
                        </div>
                        {(selectedCaseDetails?.caseOwnerName ||
                          selectedCaseDetails?.caseDueAt ||
                          selectedCaseDetails?.reminderSentAt ||
                          selectedCaseDetails?.escalatedAt) && (
                          <div className="transaction-detail-modal__muted mt-2">
                            {selectedCaseDetails?.caseOwnerName && <div>Owner: {selectedCaseDetails.caseOwnerName}</div>}
                            {selectedCaseDetails?.caseDueAt && (
                              <div className={getCaseDueState(selectedCaseDetails.caseDueAt)?.overdue ? 'transaction-workspace__danger-text' : 'transaction-workspace__info-text'}>
                                {getCaseDueState(selectedCaseDetails.caseDueAt)?.label}
                              </div>
                            )}
                            {(selectedCaseDetails?.reminderEnabled ||
                              selectedCaseDetails?.escalationEnabled ||
                              selectedCaseDetails?.reminderSentAt ||
                              selectedCaseDetails?.escalatedAt) && (
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                {selectedCaseDetails?.reminderEnabled && <Badge bg="info">Reminder enabled</Badge>}
                                {selectedCaseDetails?.reminderSentAt && <Badge bg="secondary">Reminder sent</Badge>}
                                {selectedCaseDetails?.escalationEnabled && <Badge bg="danger">Escalation enabled</Badge>}
                                {selectedCaseDetails?.escalatedAt && <Badge bg="danger">Escalated</Badge>}
                              </div>
                            )}
                            {(selectedCaseDetails?.reminderSentAt || selectedCaseDetails?.escalatedAt) && (
                              <div style={{ marginTop: '8px', color: '#94a3b8' }}>
                                {selectedCaseDetails?.reminderSentAt && <div>Reminder sent: {formatDateTime(selectedCaseDetails.reminderSentAt)}</div>}
                                {selectedCaseDetails?.escalatedAt && <div>Escalated: {formatDateTime(selectedCaseDetails.escalatedAt)}</div>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          variant="outline-light"
                          size="sm"
                          disabled={caseActionLoading}
                          onClick={() => void handleUpdateCaseStatus('Open')}
                        >
                          Mark Open
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          disabled={caseActionLoading}
                          onClick={() => void handleUpdateCaseStatus('InReview')}
                        >
                          Mark In Review
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          disabled={caseActionLoading}
                          onClick={() => void handleUpdateCaseStatus('Resolved')}
                        >
                          Mark Resolved
                        </Button>
                      </div>
                    </div>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Owner</Form.Label>
                          <Form.Select
                            className="operator-control"
                            value={caseWorkflowDraft.caseOwnerUserId}
                            onChange={(e) => updateCaseWorkflowDraft('caseOwnerUserId', e.target.value)}
                          >
                            <option value="">Unassigned</option>
                            {getCaseOwnerOptions().map((owner) => (
                              <option key={owner.id} value={owner.id}>
                                {owner.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Due</Form.Label>
                          <Form.Control
                            className="operator-control"
                            type="datetime-local"
                            value={caseWorkflowDraft.caseDueAt}
                            onChange={(e) => updateCaseWorkflowDraft('caseDueAt', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Form.Check
                          type="switch"
                          id="case-reminder-enabled"
                          label="Reminder"
                          checked={caseWorkflowDraft.reminderEnabled}
                          onChange={(e) => updateCaseWorkflowDraft('reminderEnabled', e.target.checked)}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Check
                          type="switch"
                          id="case-escalation-enabled"
                          label="Escalation"
                          checked={caseWorkflowDraft.escalationEnabled}
                          onChange={(e) => updateCaseWorkflowDraft('escalationEnabled', e.target.checked)}
                        />
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Review note</Form.Label>
                      <Form.Control
                        className="operator-control"
                        as="textarea"
                        rows={2}
                        value={caseWorkflowDraft.note}
                        onChange={(e) => updateCaseWorkflowDraft('note', e.target.value)}
                        placeholder="Add the next step, owner context, or service expectation."
                      />
                      <div className="d-flex justify-content-end mt-2">
                        <Button
                          variant="outline-info"
                          size="sm"
                          disabled={caseActionLoading || !caseWorkflowDirty}
                          onClick={() => void handleUpdateCaseWorkflow()}
                        >
                          {caseActionLoading ? 'Saving...' : 'Save review plan'}
                        </Button>
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Internal note</Form.Label>
                      <Form.Control
                        className="operator-control"
                        as="textarea"
                        rows={3}
                        value={caseNoteDraft}
                        onChange={(e) => setCaseNoteDraft(e.target.value)}
                        placeholder="Record what was checked or what happens next."
                      />
                      <div className="d-flex justify-content-end mt-2">
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={caseActionLoading || !caseNoteDraft.trim()}
                          onClick={() => void handleAddCaseNote()}
                        >
                          {caseActionLoading ? 'Saving...' : 'Save note'}
                        </Button>
                      </div>
                    </Form.Group>

                    <div>
                      <div className="transaction-detail-modal__meta-label mb-2">Review history</div>
                      {(selectedCaseDetails?.caseNotes?.length || 0) > 0 ? (
                        <div className="transaction-detail-modal__history-list" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                          {selectedCaseDetails?.caseNotes.map((caseNote) => (
                            <div key={caseNote.id} className="transaction-detail-modal__history-item">
                              <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                                <div>
                                  <div className="transaction-detail-modal__history-title">
                                    {getCaseNoteTypeLabel(caseNote.type)}
                                  </div>
                                  <div className="transaction-detail-modal__history-meta mt-1">
                                    {caseNote.authorName}
                                    {caseNote.caseStatus ? ` | ${getCaseStatusLabel(caseNote.caseStatus)}` : ''}
                                  </div>
                                </div>
                                <div className="transaction-detail-modal__history-meta" style={{ whiteSpace: 'nowrap' }}>
                                  {formatDateTime(caseNote.createdAt)}
                                </div>
                              </div>
                              <div className="transaction-detail-modal__history-body mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                                {caseNote.note}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="transaction-detail-modal__muted">
                          No team notes have been added yet.
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {selectedTransaction.webhookDeliveries && selectedTransaction.webhookDeliveries.length > 0 && (
                <Row className="mb-3">
                  <Col md={12}>
                    {renderExpandableSectionHeader(
                      'webhookDeliveries',
                      `Delivery log (${selectedTransaction.webhookDeliveries.length})`,
                      'Webhook and provider delivery attempts.',
                    )}
                    {expandedSections.webhookDeliveries && (
                      <div className="transaction-detail-modal__table-surface">
                        <div className="transaction-detail-modal__table-wrap">
                          <Table responsive className="app-data-table transaction-detail-modal__table mb-0">
                          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th>Action</th>
                              <th>Status</th>
                              <th>Attempts</th>
                              <th>Last Result</th>
                              <th>Timestamps</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedTransaction.webhookDeliveries.map((delivery) => (
                              <tr key={delivery.id}>
                                <td>
                                  <div className="app-data-table__primary">{delivery.action}</div>
                                  <div className="app-data-table__secondary">
                                    {delivery.source}
                                    {delivery.eventId ? ` | event #${delivery.eventId}` : ''}
                                  </div>
                                </td>
                                <td>
                                  <div>{delivery.status}</div>
                                  {delivery.nextAttemptAt && delivery.status !== 'Delivered' && (
                                    <div className="app-data-table__secondary">
                                      Next retry: {formatDateTime(delivery.nextAttemptAt)}
                                    </div>
                                  )}
                                </td>
                                <td>{delivery.attemptCount}</td>
                                <td>
                                  {delivery.responseStatusCode ? (
                                    <div>HTTP {delivery.responseStatusCode}</div>
                                  ) : (
                                    <div>No success response</div>
                                  )}
                                  {delivery.lastErrorCode && (
                                    <div className="transaction-workspace__danger-text app-data-table__secondary">
                                      {delivery.lastErrorCode}: {delivery.lastErrorMessage || 'Delivery failed'}
                                    </div>
                                  )}
                                </td>
                                <td className="app-data-table__secondary">
                                  <div>Created: {formatDateTime(delivery.createdAt)}</div>
                                  {delivery.lastAttemptAt && <div>Last attempt: {formatDateTime(delivery.lastAttemptAt)}</div>}
                                  {delivery.deliveredAt && <div>Delivered: {formatDateTime(delivery.deliveredAt)}</div>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
              )}

              {/* Disposable Address Section */}
              <Row className="mb-3">
                <Col md={12}>
                  {renderExpandableSectionHeader(
                    'wallets',
                    'Wallets',
                    'Funding, balance, and payout destinations.',
                  )}
                  {expandedSections.wallets && (
                    <div className="transaction-detail-modal__surface mt-3">
                      <div className="transaction-detail-modal__meta-grid">
                        <div className="transaction-detail-modal__meta-item">
                          <span className="transaction-detail-modal__meta-label">Funding address</span>
                          <strong className="transaction-detail-modal__mono">{selectedTransaction.uniqueWalletAddress}</strong>
                        </div>
                      
                      {(userRole === 'Admin' || userRole === 'SuperAgent') && (
                        <div className="transaction-detail-modal__meta-item">
                          <span className="transaction-detail-modal__meta-label">Observed balance</span>
                          {(userRole === 'Admin' || userRole === 'SuperAgent') && walletBalances[selectedTransaction.uniqueWalletAddress] && (
                            <div className="transaction-detail-modal__history-item">
                              <div>{selectedTransaction.cryptoCurrency || 'USDC'}: {walletBalances[selectedTransaction.uniqueWalletAddress].usdcBalance}</div>
                              <div>MATIC: {walletBalances[selectedTransaction.uniqueWalletAddress].maticBalance}</div>
                            </div>
                          )}
                          {(userRole === 'Admin' || userRole === 'SuperAgent') && loadingBalances && (
                            <div className="transaction-detail-modal__muted">
                              Loading balance...
                            </div>
                          )}
                        </div>
                      )}

                      {userRole === 'Admin' && (() => {
                        const user = getUserById(selectedTransaction.userId);
                        const walletLabel = user?.role === 'Agent' ? `${SUB_MERCHANT_LABEL} Wallet` : 
                                          user?.role === 'SuperAgent' ? `${MERCHANT_LABEL} Wallet` : 'User Wallet';
                        return (
                          <div className="transaction-detail-modal__meta-item">
                            <span className="transaction-detail-modal__meta-label">{walletLabel}</span>
                            <strong className="transaction-detail-modal__mono">{selectedTransaction.userWallet}</strong>
                          </div>
                        );
                      })()}
                      
                      {userRole === 'Admin' && (() => {
                        const user = getUserById(selectedTransaction.userId);
                        if (user?.role === 'Agent') {
                          const superAgent = getSuperAgentForAgent(user.id);
  
                          return (
                            <div className="transaction-detail-modal__meta-item">
                              <span className="transaction-detail-modal__meta-label">{MERCHANT_LABEL} Wallet</span>
                              <strong className="transaction-detail-modal__mono">{superAgent?.settlementWallet || 'No wallet address configured'}</strong>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
              
              {userRole === 'Admin' && (
                <Row className="mb-3">
                  <Col md={12}>
                    <div className="transaction-detail-modal__section-heading">Fee split</div>
                    <div className="transaction-detail-modal__surface">
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="transaction-detail-modal__meta-item">
                            <span className="transaction-detail-modal__meta-label">{MERCHANT_LABEL}</span>
                            <strong>{selectedTransaction.fees.superAgentPercent}%</strong>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="transaction-detail-modal__meta-item">
                            <span className="transaction-detail-modal__meta-label">{SUB_MERCHANT_LABEL}</span>
                            <strong>{selectedTransaction.fees.agentPercent}%</strong>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Generated Links Section */}
              {selectedTransaction.checkoutSessions && selectedTransaction.checkoutSessions.length > 0 && (
                <Row className="mb-3">
                  <Col md={12}>
                    {renderExpandableSectionHeader(
                      'generatedLinks',
                      `Checkout links (${selectedTransaction.checkoutSessions.length})`,
                      'Copy or verify the checkout link that was shared.',
                    )}
                    {expandedSections.generatedLinks && (
                      <div className="transaction-detail-modal__surface mt-3">
                        <div className="transaction-detail-modal__link-list">
                        {selectedTransaction.checkoutSessions.map((session, index) => {
                          const generatedLink = `${window.location.origin}/pay/${selectedTransaction.publicPaymentId || session.id}`;
                          return (
                            <div key={session.id} className="transaction-detail-modal__link-card">
                              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                                <div>
                                  <div className="transaction-detail-modal__history-title">Link #{index + 1}</div>
                                  <div className="transaction-detail-modal__history-meta mt-1">
                                    Session ID: {session.id} | Method: {session.paymentMethod} | Status: {session.status}
                                  </div>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(generatedLink).then(() => {
                                      alert('Link copied to clipboard!');
                                    }).catch(() => {
                                      alert('Failed to copy link');
                                    });
                                  }}
                                  style={{ fontSize: '0.8rem' }}
                                >
                                  Copy Link
                                </Button>
                              </div>
                              <div className="transaction-detail-modal__meta-item transaction-detail-modal__mono mt-3">
                                {generatedLink}
                              </div>
                              <div className="d-flex flex-wrap justify-content-between gap-2 mt-3 transaction-detail-modal__history-meta">
                                <span>Created: {new Date(session.createdAt).toLocaleString()}</span>
                                <span>Expires: {new Date(session.expireAt).toLocaleString()}</span>
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
              )}

              {/* Settlements Table */}
              {userRole === 'Admin' && selectedTransaction.payouts && selectedTransaction.payouts.length > 0 && (
                <Row className="mb-3">
                  <Col md={12}>
                    {renderExpandableSectionHeader(
                      'settlements',
                      `Payouts (${buildDisplayPayouts(selectedTransaction.payouts).length})`,
                      'On-chain payout records for this payment.',
                    )}
                    {expandedSections.settlements && (
                      <div className="transaction-detail-modal__table-surface">
                      <Table responsive className="app-data-table transaction-detail-modal__table mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Amount</th>
                            <th>Transaction</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {buildDisplayPayouts(selectedTransaction.payouts).map((payout) => {
                            const polygonExplorerUrl = `https://polygonscan.com/tx/${payout.txHash}`;
                            
                            return (
                              <tr key={payout.id}>
                                <td>
                                  {new Date(payout.createtAt).toLocaleString()}
                                </td>
                                <td>
                                  {payout.displayType}
                                </td>
                                <td>
                                  {payout.amount}
                                </td>
                                <td>
                                  <a 
                                    href={polygonExplorerUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="app-table-link"
                                  >
                                    View on PolygonScan
                                  </a>
                                </td>
                                <td className={payout.status === 'Failed' ? 'transaction-workspace__danger-text' : payout.status === 'Pending' ? 'transaction-workspace__warning-text' : ''}>
                                  {payout.status}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      </div>
                    )}
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TransactionList;


